import { ORG_ROLE_NAMES, type OrgRoleName } from "./auth/permissions";
import type { EnabledOrgRoles } from "./role-catalog";

export type PermissionMap = Record<string, string[]>;
export type OrgRolePermissions = Partial<Record<OrgRoleName, PermissionMap>>;

export const PERMISSION_CATALOG = {
  review: ["create", "read", "update", "delete", "share", "regenerate"],
  draft: ["create", "read", "update", "delete", "share", "regenerate", "export"],
  draftTemplate: ["create", "read", "update", "delete", "deleteSystem"],
  draftLegalRole: ["create", "read", "update", "delete", "deleteSystem"],
  draftClause: ["create", "read", "update", "delete"],
  contractType: ["create", "read", "update", "delete", "deleteSystem"],
  customRole: ["create", "read", "update", "delete", "deleteSystem"],
  kmWorkspace: ["create", "read", "update", "delete", "deleteDefault", "manageMembers"],
  kmLibrary: ["create", "read", "update", "delete"],
  kmDocument: ["upload", "read", "regenerate", "delete"],
  kmChat: ["create", "read", "update", "delete"],
  app: ["contract-review", "chat", "drafting", "regulatory", "rag", "research"],
  integration: ["manage"],
  organization: ["update", "delete"],
  member: ["create", "update", "delete"],
  invitation: ["create", "cancel"],
  team: ["create", "update", "delete"],
  ac: ["create", "read", "update", "delete"],
} as const;

export type PermissionResource = keyof typeof PERMISSION_CATALOG;

export const DEFAULT_ROLE_PERMISSIONS: Record<OrgRoleName, PermissionMap> = {
  owner: {
    review: ["create", "read", "update", "delete", "share", "regenerate"],
    draft: ["create", "read", "update", "delete", "share", "regenerate", "export"],
    draftTemplate: ["create", "read", "update", "delete", "deleteSystem"],
    draftLegalRole: ["create", "read", "update", "delete", "deleteSystem"],
    draftClause: ["create", "read", "update", "delete"],
    contractType: ["create", "read", "update", "delete", "deleteSystem"],
    customRole: ["create", "read", "update", "delete", "deleteSystem"],
    kmWorkspace: ["create", "read", "update", "delete", "deleteDefault", "manageMembers"],
    kmLibrary: ["create", "read", "update", "delete"],
    kmDocument: ["upload", "read", "regenerate", "delete"],
    kmChat: ["create", "read", "update", "delete"],
    app: ["contract-review", "chat", "drafting", "regulatory", "rag", "research"],
    integration: ["manage"],
    organization: ["update", "delete"],
    member: ["create", "update", "delete"],
    invitation: ["create", "cancel"],
    team: ["create", "update", "delete"],
    ac: ["create", "read", "update", "delete"],
  },
  admin: {
    review: ["create", "read", "update", "delete", "share", "regenerate"],
    draft: ["create", "read", "update", "delete", "share", "regenerate", "export"],
    draftTemplate: ["create", "read", "update", "delete", "deleteSystem"],
    draftLegalRole: ["create", "read", "update", "delete", "deleteSystem"],
    draftClause: ["create", "read", "update", "delete"],
    contractType: ["create", "read", "update", "delete", "deleteSystem"],
    customRole: ["create", "read", "update", "delete", "deleteSystem"],
    kmWorkspace: ["create", "read", "update", "delete", "deleteDefault", "manageMembers"],
    kmLibrary: ["create", "read", "update", "delete"],
    kmDocument: ["upload", "read", "regenerate", "delete"],
    kmChat: ["create", "read", "update", "delete"],
    app: ["contract-review", "chat", "drafting", "regulatory", "rag", "research"],
    integration: ["manage"],
    organization: ["update", "delete"],
    member: ["create", "update", "delete"],
    invitation: ["create", "cancel"],
    team: ["create", "update", "delete"],
    ac: ["create", "read", "update", "delete"],
  },
  manager: {
    review: ["create", "read", "update", "delete", "share", "regenerate"],
    draft: ["create", "read", "update", "delete", "share", "regenerate", "export"],
    draftTemplate: ["create", "read", "update", "delete"],
    draftLegalRole: ["create", "read", "update", "delete"],
    draftClause: ["create", "read", "update", "delete"],
    contractType: ["create", "read", "update", "delete"],
    customRole: ["create", "read", "update", "delete"],
    kmWorkspace: ["create", "read", "update", "delete", "manageMembers"],
    kmLibrary: ["create", "read", "update", "delete"],
    kmDocument: ["upload", "read", "regenerate", "delete"],
    kmChat: ["create", "read", "update", "delete"],
    app: ["contract-review", "chat", "rag"],
    integration: ["manage"],
    organization: ["update"],
    member: ["create", "update", "delete"],
    invitation: ["create", "cancel"],
    team: ["create", "update", "delete"],
    ac: ["create", "read", "update", "delete"],
  },
  member: {
    review: ["create", "read", "update", "share"],
    draft: ["create", "read", "update", "export"],
    draftTemplate: ["read"],
    draftLegalRole: ["read"],
    draftClause: ["read"],
    contractType: ["read"],
    customRole: ["read"],
    kmWorkspace: ["read"],
    kmLibrary: ["read"],
    kmDocument: ["read"],
    kmChat: ["create", "read"],
    app: ["contract-review", "chat", "rag"],
  },
  viewer: {
    review: ["read"],
    draft: ["read"],
    draftTemplate: ["read"],
    draftLegalRole: ["read"],
    draftClause: ["read"],
    contractType: ["read"],
    customRole: ["read"],
    kmWorkspace: ["read"],
    kmLibrary: ["read"],
    kmDocument: ["read"],
    kmChat: ["read"],
    app: ["contract-review", "chat", "rag"],
  },
};

export const OWNER_LOCKED_PERMISSIONS: PermissionMap = {
  contractType: ["deleteSystem"],
  customRole: ["deleteSystem"],
  draftTemplate: ["deleteSystem"],
  draftLegalRole: ["deleteSystem"],
  organization: ["update", "delete"],
  member: ["create", "update", "delete"],
  invitation: ["create", "cancel"],
  team: ["create", "update", "delete"],
  ac: ["create", "read", "update", "delete"],
};

function clonePermissionMap(map: PermissionMap): PermissionMap {
  return Object.fromEntries(
    Object.entries(map).map(([resource, actions]) => [resource, [...actions]])
  );
}

function isValidCatalogAction(resource: string, action: string): boolean {
  const catalogActions = PERMISSION_CATALOG[resource as PermissionResource];
  if (!catalogActions) return false;
  return (catalogActions as readonly string[]).includes(action);
}

/** Map legacy kmDocument actions when loading stored org overrides. */
function migrateLegacyKmDocumentActions(actions: string[]): string[] {
  const migrated = actions.filter(
    (action) => action !== "update" && action !== "create",
  );
  if (actions.includes("update") && !migrated.includes("regenerate")) {
    migrated.push("regenerate");
  }
  if (actions.includes("create") && !migrated.includes("upload")) {
    migrated.push("upload");
  }
  return [...new Set(migrated)];
}

function migrateRolePermissionMap(roleMap: PermissionMap): PermissionMap {
  const kmDocument = roleMap.kmDocument;
  if (!kmDocument) {
    return roleMap;
  }
  return {
    ...roleMap,
    kmDocument: migrateLegacyKmDocumentActions(kmDocument),
  };
}

export function resolveRolePermissions(
  roleKey: OrgRoleName,
  orgOverrides?: OrgRolePermissions | null
): PermissionMap {
  const defaults = clonePermissionMap(DEFAULT_ROLE_PERMISSIONS[roleKey]);
  const stored = orgOverrides?.[roleKey];
  const base = stored
    ? {
        ...defaults,
        ...Object.fromEntries(
          Object.entries(migrateRolePermissionMap(stored)).map(([resource, actions]) => [
            resource,
            [...actions],
          ])
        ),
      }
    : defaults;

  if (base.app?.includes("contract-review") && !base.app.includes("chat")) {
    base.app = [...base.app, "chat"];
  }

  if (roleKey === "owner") {
    for (const [resource, actions] of Object.entries(OWNER_LOCKED_PERMISSIONS)) {
      const existing = new Set(base[resource] ?? []);
      for (const action of actions) {
        existing.add(action);
      }
      base[resource] = [...existing];
    }
  }

  return base;
}

export function normalizeOrgRolePermissions(
  input: OrgRolePermissions | null | undefined,
  enabledOrgRoles: EnabledOrgRoles
): OrgRolePermissions {
  if (!input) return {};

  const normalized: OrgRolePermissions = {};

  for (const roleKey of ORG_ROLE_NAMES) {
    if (!enabledOrgRoles[roleKey]) continue;
    const roleInput = input[roleKey];
    if (!roleInput) continue;

    const roleMap: PermissionMap = {};
    for (const [resource, actions] of Object.entries(roleInput)) {
      if (!(resource in PERMISSION_CATALOG)) {
        throw new Error(`Unknown permission resource "${resource}"`);
      }
      if (!Array.isArray(actions)) {
        throw new Error(`Invalid permissions for resource "${resource}"`);
      }
      const migratedActions =
        resource === "kmDocument" ? migrateLegacyKmDocumentActions(actions) : actions;
      const validActions = migratedActions.filter((action) => {
        if (!isValidCatalogAction(resource, action)) {
          throw new Error(`Unknown permission "${resource}:${action}"`);
        }
        return true;
      });
      if (validActions.length > 0) {
        roleMap[resource] = [...new Set(validActions)];
      }
    }

    normalized[roleKey] = resolveRolePermissions(roleKey, { [roleKey]: roleMap });
  }

  return normalized;
}

export function getEditableRoles(enabledOrgRoles: EnabledOrgRoles): OrgRoleName[] {
  return ORG_ROLE_NAMES.filter((role) => enabledOrgRoles[role]);
}

export function parseOrgRolePermissionsFromMetadata(
  metadata: Record<string, unknown> | null | undefined
): OrgRolePermissions {
  const raw = metadata?.orgRolePermissions;
  if (!raw || typeof raw !== "object") {
    return {};
  }
  return raw as OrgRolePermissions;
}

export function roleHasResolvedPermission(
  orgRole: string | null | undefined,
  resource: string,
  action: string,
  orgRolePermissions?: OrgRolePermissions | null
): boolean {
  if (!orgRole || !ORG_ROLE_NAMES.includes(orgRole as OrgRoleName)) return false;
  const roleKey = orgRole as OrgRoleName;
  const permissions = resolveRolePermissions(roleKey, orgRolePermissions);
  const actions = permissions[resource];
  if (!actions) return false;
  return actions.includes(action);
}

export function buildResolvedOrgRolePermissions(
  orgRolePermissions: OrgRolePermissions | null | undefined,
  enabledOrgRoles: EnabledOrgRoles
): OrgRolePermissions {
  const resolved: OrgRolePermissions = {};
  for (const roleKey of getEditableRoles(enabledOrgRoles)) {
    resolved[roleKey] = resolveRolePermissions(roleKey, orgRolePermissions);
  }
  return resolved;
}
